/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import classNames from 'classnames'
import dayjs from 'dayjs'
import dynamic from 'next/dynamic'
import React, { useEffect, useState } from 'react'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'revamp/reusable-components/tooltip/tooltip'
import HorizontalNavigation from '../Common/HorizontalNavigation'
import { TValuationData } from '@/components/Stocks/Widgets/types'
import ArrowDown from '@/assets/common/arrowDown.inline.svg'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import InStockL2Heading from '@/components/Stocks/Pages/InStockDetailPageV2/InL2Heading'
import Text from '../Common/NewText'

const ChartLoader = () => (
  <div className="mb-4 h-96 animate-pulse rounded bg-gray-100" />
)

const DynamicChartWithNoSSR = dynamic(() => import('./ValuationsChart'), {
  ssr: false,
  loading: ChartLoader,
})

function ValuationBarDataPoint({
  children,
  distanceClassname,
  lineHeight,
  lineTop,
  boxTop,
  // boxWidth,
  boxColor,
  circleColor,
  lineColor,
}: {
  children: JSX.Element
  distanceClassname?: string
  lineHeight?: string
  lineTop?: string
  boxTop?: string
  // boxWidth?: string
  boxColor?: string
  circleColor?: string
  lineColor?: string
}) {
  return (
    <div
      className={classNames(
        'border-brand-white absolute -top-[1px] h-4 w-4 rounded-full border-[2px]',
        distanceClassname ?? 'left-2/10',
        circleColor ?? 'bg-brand-grey-light',
      )}
    >
      <div
        className={classNames(
          'absolute z-10 min-w-max rounded-lg border-0 p-2',
          boxColor ?? 'bg-brand-tertiary-grey',
        )}
        style={{
          left: '-40px',
          top: boxTop ?? '-96px',
        }}
      >
        {children}
      </div>
      <div
        style={{ height: lineHeight ?? '50px', top: lineTop ?? '-40px' }}
        className={classNames(
          'absolute  left-[5px] border-l-[1px] border-dashed',
          lineColor ?? 'border-brand-grey-light',
        )}
      />
    </div>
  )
}
/* eslint-disable max-lines-per-function */
export default React.memo(function Valuations({
  data,
  name,
}: {
  data: TValuationData
  name: string
}) {
  const [chartMode, setChartMode] = useState<{
    duration: 'annually' | 'quarterly'
    range: string | null
  }>({
    duration: 'annually',
    range: data.historic_pe_ratio?.annually?.[0].label ?? null,
  })

  const [tab, setTab] = useState<string>('overview')
  useEffect(() => {
    return () => {
      setTab('overview')
    }
  }, [])
  const [showAll, setShowAll] = useState<boolean>(false)
  // const toUpperCaseLetter = str =>
  //   str.replace(/^./, match => match.toUpperCase())

  // const showTag = tagName => {
  //   let modifiedTagName = tagName
  //   let compareTxt = ''
  //   let listTagName = modifiedTagName
  //   switch (tagName) {
  //     case 'overvalued':
  //       modifiedTagName = toUpperCaseLetter(tagName)
  //       compareTxt = 'more than'
  //       listTagName = toUpperCaseLetter(tagName)
  //       break
  //     case 'undervalued':
  //       modifiedTagName = toUpperCaseLetter(tagName)
  //       compareTxt = 'less than'
  //       listTagName = toUpperCaseLetter(tagName)
  //       break
  //     case 'fair-price':
  //       modifiedTagName = `Fair price`
  //       compareTxt = 'similar to'
  //       listTagName = `of fair price`
  //       break
  //     default:
  //       break
  //   }
  //   return {
  //     modifiedTagName,
  //     compareTxt,
  //     listTagName,
  //   }
  // }

  const industyGreater = !!(
    data.pe_ratio?.industry?.ratio &&
    data.pe_ratio.today?.ratio &&
    !!(data.pe_ratio.industry.ratio > data.pe_ratio.today.ratio)
  )
  return (
    <div className="bg-brand-white">
      <div className="flex justify-between">
        {name && (
          <InStockL2Heading
            heading={`${name} Valuation`}
            subheading={`Track how the P/E of ${name} has moved over time to understand valuation trends.`}
          />
        )}
        <Tooltip>
          <TooltipTrigger>
            <StencilCDNImage
              src="https://indcdn.indmoney.com/public/images/info_new.png"
              width={18}
              height={18}
              onClick={() => {}}
            />
          </TooltipTrigger>

          <TooltipContent>
            <div
              className="bg-brand-white border-1 shadow-one w-56 p-2 text-xs"
              dangerouslySetInnerHTML={{
                __html:
                  'The content present here is for informational purposes only, you should not construe any such information as investment advisory.',
              }}
            />
          </TooltipContent>
        </Tooltip>
      </div>

      {typeof data.no_of_year === 'number' && name && (
        <p className="text-brand-grey-base text-[13px] font-normal">
          {name} in the last {data.no_of_year} year
          {data.no_of_year > 1 ? 's' : ''}
        </p>
      )}
      <div className="my-4 flex w-full justify-center">
        <div className="mb-4 mt-2 w-full md:w-1/2">
          <HorizontalNavigation
            titleClassName="text-base font-normal"
            data={[
              {
                title: 'Overview',
                selected: tab === 'overview',
              },
              {
                title: 'Trends',
                selected: tab === 'trends',
              },
            ]}
            onSelect={title => {
              setTab(title === 'Overview' ? 'overview' : 'trends')
            }}
          />
        </div>
      </div>
      {tab === 'overview' ? (
        <div>
          <div>
            <div
              className="relative mt-56 h-3 w-full rounded-lg py-1"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, #45BE61 32.9%, #E95A0A 99.48%)',
              }}
            >
              {typeof data.pe_ratio?.lowest?.ratio === 'number' &&
                typeof data.pe_ratio.lowest.date === 'string' && (
                  <ValuationBarDataPoint>
                    <>
                      <p className="text-brand-grey-base text-[13px]">
                        Lowest ({data.pe_ratio.lowest.ratio.toFixed(2)}x)
                      </p>
                      <p className="text-brand-grey-base text-[11px]">
                        {dayjs(data.pe_ratio.lowest.date).format(
                          'MMMM D, YYYY',
                        )}
                      </p>
                    </>
                  </ValuationBarDataPoint>
                )}
              {industyGreater ? (
                <>
                  {typeof data.pe_ratio?.today?.ratio === 'number' &&
                    typeof data.pe_ratio.today.date === 'string' && (
                      <ValuationBarDataPoint
                        distanceClassname="left-4/10"
                        lineHeight="160px"
                        boxTop="-200px"
                        lineTop="-160px"
                        boxColor="bg-brand-tertiary-green"
                        lineColor="border-brand-green-dark"
                        circleColor="bg-brand-green-dark"
                      >
                        <>
                          <p className="text-brand-green-dark text-[13px]">
                            Today ({data.pe_ratio.today.ratio.toFixed(2)}x)
                          </p>
                          <p className="text-brand-green-dark text-[11px]">
                            {dayjs(data.pe_ratio.today.date).format(
                              'MMMM D, YYYY',
                            )}
                          </p>
                        </>
                      </ValuationBarDataPoint>
                    )}
                  {typeof data.pe_ratio?.industry?.ratio === 'number' &&
                    typeof data.pe_ratio.industry.date === 'string' && (
                      <ValuationBarDataPoint
                        distanceClassname="left-6/10"
                        lineHeight="110px"
                        boxTop="-160px"
                        lineTop="-110px"
                        boxColor="bg-brand-tertiary-blue"
                        lineColor="border-chart-blue"
                        circleColor="bg-chart-blue"
                      >
                        <>
                          <p className="text-chart-blue text-[13px] ">
                            Industry ({data.pe_ratio.industry.ratio.toFixed(2)}
                            x)
                          </p>
                          <p className="text-chart-blue text-[11px]">
                            {dayjs(data.pe_ratio.industry.date).format(
                              'MMMM D, YYYY',
                            )}
                          </p>
                        </>
                      </ValuationBarDataPoint>
                    )}
                </>
              ) : (
                <>
                  {typeof data.pe_ratio?.industry?.ratio === 'number' &&
                    typeof data.pe_ratio.industry.date === 'string' && (
                      <ValuationBarDataPoint
                        distanceClassname="left-4/10"
                        lineHeight="160px"
                        boxTop="-200px"
                        lineTop="-160px"
                        boxColor="bg-brand-tertiary-blue"
                        lineColor="border-chart-blue"
                        circleColor="bg-chart-blue"
                      >
                        <>
                          <p className="text-chart-blue text-[13px] ">
                            Industry ({data.pe_ratio.industry.ratio.toFixed(2)}
                            x)
                          </p>
                          <p className="text-chart-blue text-[11px]">
                            {dayjs(data.pe_ratio.industry.date).format(
                              'MMMM D, YYYY',
                            )}
                          </p>
                        </>
                      </ValuationBarDataPoint>
                    )}
                  {typeof data.pe_ratio?.today?.ratio === 'number' &&
                    typeof data.pe_ratio.today.date === 'string' && (
                      <ValuationBarDataPoint
                        distanceClassname="left-6/10"
                        lineHeight="110px"
                        boxTop="-160px"
                        lineTop="-110px"
                        boxColor="bg-brand-tertiary-green"
                        lineColor="border-brand-green-dark"
                        circleColor="bg-brand-green-dark"
                      >
                        <>
                          <p className="text-brand-green-dark text-[13px]">
                            Today ({data.pe_ratio.today.ratio.toFixed(2)}x)
                          </p>
                          <p className="text-brand-green-dark text-[11px]">
                            {dayjs(data.pe_ratio.today.date).format(
                              'MMMM D, YYYY',
                            )}
                          </p>
                        </>
                      </ValuationBarDataPoint>
                    )}
                </>
              )}
              {typeof data.pe_ratio?.highest?.ratio === 'number' &&
                typeof data.pe_ratio.highest.date === 'string' && (
                  <ValuationBarDataPoint
                    distanceClassname="left-8/10 md:left-8/10"
                    lineHeight="190px"
                    boxTop="-230px"
                    lineTop="-190px"
                    // boxWidth="150px"
                  >
                    <>
                      <p className="text-brand-grey-base text-[13px]">
                        Highest ({data.pe_ratio.highest.ratio.toFixed(2)}x)
                      </p>
                      <p className="text-brand-grey-base text-[11px]">
                        {dayjs(data.pe_ratio.highest.date).format(
                          'MMMM D, YYYY',
                        )}
                      </p>
                    </>
                  </ValuationBarDataPoint>
                )}
            </div>
            <div className="text-brand-grey-base mt-2 flex w-full items-center justify-between px-4 text-xs">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>
          {data.pe_ratio?.today?.ratio && (
            <div className="mt-6">
              <Text appearance="body1" addClassName="text-brand-grey-base">
                Today’s Price to Earnings Ratio:{' '}
                {data.pe_ratio.today.ratio.toFixed(2)}x
              </Text>
            </div>
          )}
        </div>
      ) : (
        <div>
          {!!data.historic_pe_ratio?.[chartMode.duration]?.length && (
            <>
              {name && (
                <p className="mt-4 font-medium">Historical trend in {name}</p>
              )}
              <DynamicChartWithNoSSR data={data} chartMode={chartMode} />
              <div className="mt-2 flex w-full items-center justify-center">
                <HorizontalNavigation
                  data={
                    data.historic_pe_ratio[chartMode.duration]?.map(item => {
                      return {
                        title: item.label ?? '',
                        selected: item.label === chartMode.range,
                      }
                    }) ?? []
                  }
                  onSelect={title => {
                    setChartMode(prev => {
                      return {
                        ...prev,
                        range: title,
                      }
                    })
                  }}
                  labelClassName="px-6 py-2 text-sm flex items-center justify-center"
                  titleClassName="font-normal flex items-center"
                />
              </div>
              <div className="my-4 flex w-full justify-center">
                <div className="my-2 w-full md:w-1/2">
                  <HorizontalNavigation
                    titleClassName="text-base font-normal"
                    data={[
                      {
                        title: 'Annual',
                        selected: chartMode.duration === 'annually',
                      },
                      {
                        title: 'Quarterly',
                        selected: chartMode.duration === 'quarterly',
                      },
                    ]}
                    onSelect={title => {
                      if (title === 'Annual') {
                        setChartMode({
                          duration: 'annually',
                          range:
                            data.historic_pe_ratio?.annually?.[0].label ?? null,
                        })
                      } else {
                        setChartMode({
                          duration: 'quarterly',
                          range:
                            data.historic_pe_ratio?.annually?.[0].label ??
                            data.historic_pe_ratio?.quarterly?.[0].label ??
                            null,
                        })
                      }
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {showAll && (
        <div>
          {!!data.peer_pe_ratio?.length && (
            <div className="bg-brand-tertiary-grey mt-6 rounded-lg p-2">
              {data.peer_pe_ratio.map((item, idx) => {
                if (!item.name || !item.ratio) return null
                return (
                  <div
                    className={classNames('flex justify-between p-2 text-sm', {
                      'border-b-1':
                        idx !== (data.peer_pe_ratio?.length ?? 0) - 1,
                    })}
                    key={item.name}
                  >
                    <p className="text-brand-grey-base">{item.name}</p>
                    <p className="font-medium">{item.ratio}x</p>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
})
